import { useSearchOrganizationsMutation } from '@/modules/organisations/services/search'
import {
  Organizations,
  SearchTypesReq,
} from '@/modules/organisations/types/searchTypes'
import { useGetUserProfileQuery } from '@/modules/user/services/userService'
import { UserTypes } from '@/types/typeUser'
import cn from '@/utils/cn'
import AddIcon from '@mui/icons-material/Add'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import {
  CircularProgress,
  Fab,
  Link as MiuLink,
  Pagination,
  Typography,
  useMediaQuery,
} from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import * as React from 'react'
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

const s = cn('organizations')

interface IUsersTable {
  showAllOrganizations: boolean
  isSearch: boolean
  searchValue: string
  setSearchValue: Dispatch<SetStateAction<string>>
}

const OrganizationsTable: FC<IUsersTable> = ({
  showAllOrganizations,
  isSearch,
  searchValue,
  setSearchValue,
}) => {
  const navigate = useNavigate()

  const isIphone = navigator.userAgent.includes('iPhone')

  const [getOrganizationsService, { isLoading: isLoadingSearch }] =
    useSearchOrganizationsMutation()
  const { data: currentUserData } = useGetUserProfileQuery('0', {
    refetchOnMountOrArgChange: true,
  })
  const currentUserType = currentUserData?.data.typeId

  const [organizations, setOrganizations] = useState<Organizations[]>([])
  const [page, setPage] = React.useState(
    Number(localStorage.getItem('pageOrganizations')) || 1
  )
  const [pages, setPages] = React.useState(1)
  const [count, setCount] = React.useState(0)

  const handleChangePage = (event: unknown, newPage: number) => {
    localStorage.setItem('pageOrganizations', String(newPage))
    setPage(newPage)
  }

  const onGetUsers = async () => {
    try {
      const usersSearchData: SearchTypesReq = {
        value: !searchValue.trim().length ? '' : searchValue,
        showDeleted: showAllOrganizations,
        page: page,
        newSearch: true,
      }
      const res = await getOrganizationsService(usersSearchData).unwrap()
      if (res.success) {
        setOrganizations(res.data)
        setPage(res.page || 1)
        setPages(res.pages || 1)
        setCount(res.count ?? 0)
      }
      if (!searchValue.trim().length) {
        setSearchValue('')
      }
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    onGetUsers().then()
  }, [page, showAllOrganizations, isSearch])

  const isTablet = useMediaQuery('(max-width:870px)')

  return (
    <Box
      display='flex'
      justifyContent='center'
      alignItems='center'
      position='relative'
    >
      <CircularProgress
        className={isLoadingSearch ? s('show-loader') : s('hide-loader')}
      />
      <Box
        width='100%'
        className={isLoadingSearch ? s('table-loading') : ''}
      >
        <Paper
          sx={{
            width: '100%',
            mt: 3,
            pb: 2,
          }}
          elevation={6}
        >
          <TableContainer
            sx={{
              height: `calc(100vh - ${
                currentUserType === UserTypes.user
                  ? 210
                  : isIphone
                    ? document.documentElement.clientHeight - 280
                    : isTablet
                      ? 370
                      : 270
              }px)`,
              overflowY: 'auto',
            }}
            className={s('tableContainer')}
          >
            <Table size='small'>
              <TableBody>
                {organizations.length === 0 ? (
                  <TableRow>
                    <TableCell
                      component='th'
                      scope='row'
                    >
                      <Typography textAlign='center'>
                        Ни одной записи не найдено
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  organizations.map((organization) => {
                    return (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={organization.id}
                        className={s({ deleted: organization.deleted })}
                      >
                        <TableCell padding='checkbox'>
                          {organization.deleted ? (
                            <RemoveCircleIcon htmlColor='#a9a9a9' />
                          ) : (
                            <CheckCircleIcon htmlColor='#0072BC' />
                          )}
                        </TableCell>
                        <TableCell
                          component='th'
                          scope='row'
                        >
                          <Link
                            to={`/organizations/${organization.id}`}
                            className={s('link', {
                              deleted: organization.deleted,
                            })}
                          >
                            {organization.name || '---'}
                            <span>{organization.address}</span>
                          </Link>
                        </TableCell>
                        <TableCell
                          align='right'
                          onClick={() =>
                            navigate(`/organizations/${organization.id}`)
                          }
                          sx={{
                            color: organization.deleted
                              ? '#a9a9a9'
                              : 'rgb(0 0 0 / 87%)',
                            cursor: 'pointer',
                          }}
                        >
                          {organization.itn ? `ИНН ${organization.itn}` : ''}
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box mt={2} />
          <Box
            display='flex'
            width='100%'
            justifyContent='flex-end'
          >
            {count <= 25 ? (
              <Box height={26} />
            ) : (
              <Pagination
                count={pages}
                page={page}
                onChange={handleChangePage}
                size='small'
                siblingCount={1}
                boundaryCount={2}
                color={'primary'}
              />
            )}
          </Box>
        </Paper>
        <Box mt={2} />
        {currentUserType === UserTypes.user ||
        currentUserType === UserTypes.admin ? null : (
          <Box
            display='flex'
            alignItems='center'
            gap={2}
            pb={isIphone ? 4 : 2}
          >
            <Fab
              style={{ zIndex: 1 }}
              onClick={() => navigate('/organizations/new')}
              color='green'
              aria-label='add'
            >
              <AddIcon />
            </Fab>
            <MiuLink
              component='button'
              variant='body1'
              onClick={() => navigate('/organizations/new')}
            >
              Добавить организацию
            </MiuLink>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default OrganizationsTable
